import React, { useState, useEffect, useRef } from 'react';

import { DialogTitle, DialogContent, DialogActions, Dialog } from '../AlmeDialog/AlmeDialog';
import { FocusComponentProps } from '../../models/ComponentProps';

import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Button, Typography } from '@material-ui/core';
import { AlmeCloseConfig } from './AlmeCloseConfig';
import { AlmeCloseIcon } from '../AlmeCloseIcon/AlmeCloseIcon';
import { AlmeIvaTypeProp } from '../../models/AlmeIvaTypeMapping';
import { FormattedMessage, useIntl } from 'react-intl';
import {useFocusStyles} from '../FocusVisibleStyle';

const useStyles = makeStyles(() =>
    createStyles({
        closeContainer: {
            display: 'inline-block',
        },
    }),
);

interface AlmeCloseProps {
    onResetIva: (isOpen: boolean, sessionEndReason: string, shouldResetIsInitialized?: boolean) => void;
    almeConfig: AlmeCloseConfig;
    setIsDialogOpen: (isOpen: boolean) => void;
    ivaType?: AlmeIvaTypeProp;
    focusProps?: FocusComponentProps;
    isDialogOpen?: boolean;
}

const AlmeClose: React.FC<AlmeCloseProps> = (props: AlmeCloseProps) => {
    const intl = useIntl();
    const [open, setOpen] = React.useState(false);
    const [shouldFocus, setShouldFocus] = useState<boolean>(false);
    const closeRef = useRef<HTMLButtonElement>(null);

    const classes = useStyles();
    const Focusclasses = useFocusStyles();

    useEffect(() => {
        if (!props.isDialogOpen && shouldFocus) {
            closeRef.current?.focus();
            setShouldFocus(false);
        }
    }, [props.isDialogOpen, shouldFocus]);


    const handleClickOpen = (): void => {
        setOpen(true);
    };

    const handleCancelClose = (): void => {
        setOpen(false);
        setShouldFocus(true);
    };

    const handleClose = (): void => {
        const data = {
            message: {
                type: 'CloseIva',
            },
        };
        window.top.postMessage(data, "*");
        setOpen(false);
        props.onResetIva(false, "UserEndedSession", true);
    };

    if (!props.almeConfig.close.closeIcon.isCloseEnabled || props.ivaType === 'popup') {
        return null;
    }

    const accessibilityAttrs = {
        role: 'dialog',
    };

    return (
        <div className={classes.closeContainer}>
            <AlmeCloseIcon className={Focusclasses.visibleFocus}
                id='alme-close' aria-label={
                intl.formatMessage({
                    id: "closeIcon.closeIconAriaLabel",
                    defaultMessage: 'Default close icon aria label',
                    description: 'AlmeClose icon aria label',
                })}
                almeConfig={props.almeConfig.close} onClose={handleClickOpen}
                focusProps={props.focusProps} forwardRef={closeRef}
            />
            <Dialog
                disableEnforceFocus
                id="alme-close-dialog"
                aria-labelledby="alme-close-dialog-title"
                aria-describedby="alme-close-dialog-description"
                open={open}
                onClose={handleCancelClose}
                setIsDialogOpen={props.setIsDialogOpen}
                fullWidth
                {...accessibilityAttrs}
            >
                <DialogTitle id="alme-close-dialog-title" almeConfig={props.almeConfig.close} onClose={handleCancelClose}>
                    <FormattedMessage
                        id="close.DialogTitleText"
                        defaultMessage="Default Close Dialog title text"
                        description="AlmeClose dialog title text" />
                </DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom id="alme-close-dialog-description">
                        <FormattedMessage
                            id="close.DialogBodyText"
                            defaultMessage="Default Close Dialog body text"
                            description="AlmeClose dialog body text" />
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button id='alme-close-dialog-cancel-button'
                        className={Focusclasses.visibleFocus}
                        variant={props.almeConfig.close.dialog.styles.buttons.variant}
                        color={props.almeConfig.close.dialog.styles.buttons.color}
                        onClick={handleCancelClose}>
                        <FormattedMessage
                            id="close.declineButtonText"
                            defaultMessage="Default Cancel"
                            description="AlmeClose decline button text" />
                    </Button>
                    <Button id='alme-close-dialog-close-button'
                        className={Focusclasses.visibleFocus}
                        variant={props.almeConfig.close.dialog.styles.buttons.variant}
                        color={props.almeConfig.close.dialog.styles.buttons.color}
                        onClick={handleClose}>
                        <FormattedMessage
                            id="close.acceptButtonText"
                            defaultMessage="Default Close"
                            description="Alme close accept button text" />
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AlmeClose;
